import { Package, MapPin, AlertTriangle, TrendingDown, TrendingUp } from 'lucide-react';
import type { Consumable, StockUnit } from '@yawi/types';
import { useI18n } from '@/shared/i18n';

interface ConsumableCardProps {
  consumable: Consumable;
  onClick?: () => void;
  onStockIn?: () => void;
  onStockOut?: () => void;
  showActions?: boolean;
}

const unitLabels: Record<StockUnit, string> = {
  UNITS: 'unidades',
  LITERS: 'litros',
  KILOGRAMS: 'kg',
  METERS: 'metros',
  BOXES: 'cajas',
  PACKS: 'paquetes',
};

export const ConsumableCard = ({
  consumable,
  onClick,
  onStockIn,
  onStockOut,
  showActions = true,
}: ConsumableCardProps) => {
  const { t } = useI18n();

  const stockPercentage = consumable.maximumStock
    ? (consumable.currentStock / consumable.maximumStock) * 100
    : (consumable.currentStock / (consumable.minimumStock * 3)) * 100;

  const getStockColor = () => {
    if (consumable.isLowStock) return 'bg-red-500';
    if (stockPercentage < 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow ${
        onClick ? 'cursor-pointer' : ''
      }`}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 font-mono">{consumable.code}</span>
          </div>
          <h3 className="font-semibold text-slate-800 truncate">{consumable.name}</h3>
        </div>
        {consumable.isLowStock && (
          <div className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
            <AlertTriangle className="w-3 h-3" />
            <span className="text-xs font-medium">{t('inventory.lowStock')}</span>
          </div>
        )}
      </div>

      {/* Stock Level */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-2xl font-bold text-slate-800">
            {consumable.currentStock}
          </span>
          <span className="text-sm text-slate-500">
            {unitLabels[consumable.unit]}
          </span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${getStockColor()} transition-all`}
            style={{ width: `${Math.min(stockPercentage, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
          <span>{t('inventory.min')}: {consumable.minimumStock}</span>
          {consumable.maximumStock && (
            <span>{t('inventory.max')}: {consumable.maximumStock}</span>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="space-y-1 text-xs text-slate-500">
        {consumable.location && (
          <div className="flex items-center gap-2">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{consumable.location}</span>
          </div>
        )}
        {consumable.unitCost !== null && (
          <div className="flex items-center justify-between">
            <span>{t('inventory.unitCost')}:</span>
            <span className="font-medium text-slate-700">
              ${consumable.unitCost.toLocaleString('es-MX')}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      {showActions && (onStockIn || onStockOut) && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
          {onStockIn && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStockIn();
              }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
            >
              <TrendingUp className="w-4 h-4" />
              {t('inventory.stockIn')}
            </button>
          )}
          {onStockOut && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStockOut();
              }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
            >
              <TrendingDown className="w-4 h-4" />
              {t('inventory.stockOut')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
